<template>
  <div class="pedidos-page">
    <modal_pdf_pedido ref="modal_pdf_pedido"/>

    <nav class="page-breadcrumb">
      <router-link :to="{ name: 'dashboard-ptc' }" class="breadcrumb-link">
        <feather-icon icon="HomeIcon" size="14" />
        <span>Operações</span>
      </router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="breadcrumb-separator" />
      <span class="breadcrumb-current">Pedidos</span>
    </nav>

    <!-- Filtro de tipo de pedido: visão exclusiva por tipo. Só aparece para
         perfis com mais de um tipo permitido (ex.: Representante só tem Doação
         → sem filtro). -->
    <div v-if="tiposPermitidos.length > 1" class="tipo-filter-bar">
      <span class="tipo-filter-label">Tipo de pedido:</span>
      <button
        v-for="tipo in tiposPermitidos"
        :key="tipo.tipopedidoid"
        class="tipo-pill"
        :class="{ active: tipoSelecionado === tipo.tipopedidoid }"
        @click="selecionarTipo(tipo.tipopedidoid)"
      >
        {{ rotuloTipo(tipo) }}
      </button>
    </div>

    <KpiCards
      v-if="activeTab === 0"
      :status-summary="kpiStatusSummary"
      :selected-status="statusFilter"
      :status-loading="kpiStatusLoading"
      :status-error="kpiStatusError"
      :disabled="isLoading"
      :show-backorders="activeTab === 0"
      :backorders-selected="somenteBackorders"
      :backorders-count="totalBackorders"
      :backorders-loading="backordersLoading"
      :backorders-error="backordersError"
      @filter="handleKpiFilter"
      @backorders="handleBackordersFilter"
      @retry-backorders="fetchBackordersCount"
      @retry-status="fetchStatusSummaryForKpi"
    />

    <TopActionBar
      :current-view="currentView"
      :active-filters-count="activeFiltersCount"
      :active-tab="activeTab"
      :total-pedidos="totalPedidos"
      :total-rascunhos="totalRascunhos"
      :is-loading="isLoading"
      :is-exporting="isExporting"
      :page-size="activeMeta.pageSize || 20"
      @toggle-filters="showFiltersDrawer = true"
      @change-view="changeView"
      @change-tab="changeTab"
      @change-page-size="handlePageSizeChange"
      @new-order="$router.push({ name: 'pedidos-vendas-create' })"
      @export="handleExport"
    />

    <FiltersDrawer
      :is-open="showFiltersDrawer"
      :filters="filtersData"
      @close="showFiltersDrawer = false"
      @apply="applyFilters"
      @clear="handleClearFilters"
    />

    <div class="content-container">
      <div v-show="activeTab === 0">
        <div v-show="currentView === 'list'">
          <pedidos
            ref="listPedidos"
            :hide-header-controls="true"
            @exporting-change="isExporting = $event"
            @paginateFilters="paginateFiltersPedidos"
            @view-details="viewOrderDetails"
            @generate-pdf="generatePdf"
          />
        </div>
        <template v-if="currentView === 'kanban'">
          <KanbanView
            :orders="listingRows"
            @view-details="viewOrderDetails"
          />
        </template>
        <template v-else-if="currentView === 'cards'">
          <CardsView
            :orders="listingRows"
            @view-details="viewOrderDetails"
            @generate-pdf="generatePdf"
          />
        </template>
      </div>
      <div v-if="$store.getters['preferencias/rascunhoPermitido']" v-show="activeTab === 1">
        <rascunhos
          :hide-header-controls="true"
          @paginateFilters="paginateFiltersRascunhos"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Modal_pdf_pedido from '@/views/app/operacoes/pedidosVendas/modal/modal_pdf_pedido.vue';
import Pedidos from '@/views/app/operacoes/pedidosVendas/pedidos.vue';
import Rascunhos from '@/views/app/operacoes/pedidosVendas/rascunhos.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import KpiCards from './components/KpiCards.vue';
import TopActionBar from './components/TopActionBar.vue';
import FiltersDrawer from './components/FiltersDrawer.vue';
import KanbanView from './components/KanbanView.vue';
import CardsView from './components/CardsView.vue';
import StatusPedidoService from '@/router/status-pedidos/status-pedidos.service';
import SLSTipoPedidoService from '@/services/SistemaDKP/SLSTipoPedido.service';
import { nomeCategoria } from '@/libs/perfilCliente';
import { pedidoListingRows, backorderMode } from '@/utils/pedidoListingRows';

const VIEW_STORAGE_KEY = 'pedidosVendas_viewPreference';
const statusPedidoService = new StatusPedidoService();
const slsTipoPedidoService = new SLSTipoPedidoService();

export default {
  components: {
    KpiCards,
    TopActionBar,
    FiltersDrawer,
    KanbanView,
    CardsView,
    Modal_pdf_pedido,
    Pedidos,
    Rascunhos,
  },
  data() {
    return {
      activeTab: 0,
      isExporting: false,
      currentView: 'list',
      showFiltersDrawer: false,
      statusFilter: null,
      somenteBackorders: false,
      totalBackorders: null,
      backordersLoading: false,
      backordersError: '',
      backordersRequest: 0,
      tiposPermitidos: [],
      tipoSelecionado: null,
      filtersData: {
        dataInicial: '',
        dataFinal: '',
        prePedido: null,
        cliente: null,
        razaoSocial: '',
        pedidoErp: null,
        status: null,
      },
      kpiStatusSummary: null,
      kpiStatusLoading: false,
      kpiStatusError: '',
      kpiStatusRequest: 0,
    };
  },
  computed: {
    ...mapGetters('SLS_PedidosVendas', [
      'getFilters',
      'getFiltersRascunhos',
      'getPedidoVendas',
      'getMeta',
      'getMetaRascunhos',
    ]),
    isLoading() {
      return this.$store.getters['SLS_PedidosVendas/getIsLoading'];
    },
    // Pedidos e rascunhos agora têm meta separada na store — sem cache manual.
    totalPedidos() {
      return this.getMeta?.total || 0;
    },
    totalRascunhos() {
      return this.getMetaRascunhos?.total || 0;
    },
    activeMeta() {
      return this.activeTab === 0 ? this.getMeta : this.getMetaRascunhos;
    },
    listingRows() {
      return pedidoListingRows(this.getPedidoVendas, 'operacoes', this.getFilters?.somenteBackorders);
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtersData.prePedido) count++;
      if (this.filtersData.pedidoErp) count++;
      if (this.filtersData.cliente) count++;
      if (this.filtersData.razaoSocial) count++;
      if (this.filtersData.status) count++;
      if (this.filtersData.dataInicial) count++;
      if (this.filtersData.dataFinal) count++;
      if (this.statusFilter) count++;
      if (this.somenteBackorders) count++;
      return count;
    },
  },
  methods: {
    // Rótulo curto do filtro por tipo: WS → Venda, WC → Certificação,
    // WD → Doação; tipos fora do mapa caem na descrição do cadastro.
    rotuloTipo(tipo) {
      const nome = nomeCategoria(tipo.tipopedidoid);
      if (nome && nome !== tipo.tipopedidoid) return nome;
      return tipo.tipopedidodescricao || tipo.tipopedidoid;
    },
    ...mapActions('SLS_PedidosVendas', [
      'setFilters',
      'setFiltersRascunhos',
      'replaceFilters',
      'replaceFiltersRascunhos',
      'retornaPedidoVendas',
      'retornaRascunhosPedidoVendas',
      'clearFilters',
    ]),

    // A lista permanece montada para exportar sem trocar a visão selecionada.
    async handleExport() {
      if (this.isExporting) return;
      const lista = this.$refs.listPedidos;
      if (lista && typeof lista.exportData === 'function') {
        await lista.exportData();
      }
    },

    // Carrega os tipos de pedido permitidos ao perfil ativo e define o tipo
    // padrão: "Venda padrão" (WS) quando permitido, senão o único/primeiro.
    async carregarTiposPermitidos() {
      try {
        const response = await slsTipoPedidoService.index({ perPage: 100, somentePermitidos: 1 });
        const items = Array.isArray(response) ? response : (response?.data || []);
        this.tiposPermitidos = items.filter((t) => t && t.tipopedidoid);
        if (this.tiposPermitidos.length) {
          const padrao = this.tiposPermitidos.find((t) => t.tipopedidoid === 'WS');
          this.tipoSelecionado = (padrao || this.tiposPermitidos[0]).tipopedidoid;
          this.filtersData.tipopedidoid = this.tipoSelecionado;
        }
      } catch (e) {
        console.warn('Não foi possível carregar os tipos de pedido permitidos:', e);
        this.tiposPermitidos = [];
        this.tipoSelecionado = null;
      }
    },

    async selecionarTipo(tipopedidoid) {
      if (this.isLoading || this.tipoSelecionado === tipopedidoid) return;
      this.tipoSelecionado = tipopedidoid;
      this.filtersData.tipopedidoid = tipopedidoid;
      this.filtersData.page = 1;
      await this.search();
    },

    loadViewPreference() {
      try {
        const saved = localStorage.getItem(VIEW_STORAGE_KEY);
        if (saved && ['list', 'kanban', 'cards'].includes(saved)) {
          this.currentView = saved;
        }
      } catch (e) {
        console.warn('Could not load view preference');
      }
    },

    saveViewPreference() {
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, this.currentView);
      } catch (e) {
        console.warn('Could not save view preference');
      }
    },

    changeView(view) {
      this.currentView = view;
      this.saveViewPreference();
    },

    async handlePageSizeChange(newSize) {
      const currentFilters = this.activeTab === 0
        ? (this.getFilters || {})
        : (this.getFiltersRascunhos || {});
      const mergedFilters = { ...currentFilters, pageSize: newSize, page: 1 };

      if (this.activeTab === 0) {
        await this.setFilters(mergedFilters);
        await this.retornaPedidoVendas();
      } else {
        await this.setFiltersRascunhos(mergedFilters);
        await this.retornaRascunhosPedidoVendas();
      }
    },

    verificarPerfilUsuario() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));

        const filtros = {};

        if (perfilLogado.perfil_acesso_nome === 'Representante') {
          const representanteData = JSON.parse(localStorage.getItem('representanteData'));
          if (representanteData && representanteData.representanteid) {
            filtros.perfilTipo = 'Representante';
            filtros.perfilId = representanteData.representanteid;
          }
        }

        // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
        if (['Cliente', 'ClienteFinal'].includes(perfilLogado.perfil_acesso_nome)) {
          const clienteData = JSON.parse(localStorage.getItem('clienteData'));
          if (clienteData && clienteData.cliente && clienteData.cliente.clienteid) {
            filtros.perfilTipo = perfilLogado.perfil_acesso_nome;
            filtros.perfilId = clienteData.cliente.clienteid;
          }
        }

        return filtros;
      } catch (error) {
        console.error('Erro ao verificar perfil do usuário:', error);
        return {};
      }
    },

    async fetchStatusSummaryForKpi() {
      const request = ++this.kpiStatusRequest;
      this.kpiStatusLoading = true;
      this.kpiStatusError = '';
      try {
        const filtrosPerfil = { ...this.getFilters, ...this.verificarPerfilUsuario() };
        // The category distribution and BO count share non-status filters.
        ['status', 'pedidoSituacao', 'somenteBackorders', 'contagemBackorders',
          'page', 'pageSize', 'limit', 'offset'].forEach(key => { delete filtrosPerfil[key]; });
        if (this.tipoSelecionado) filtrosPerfil.tipopedidoid = this.tipoSelecionado;
        const response = await statusPedidoService.findAll(filtrosPerfil);
        if (!Array.isArray(response)) throw new Error('Resposta inválida no resumo de situações.');
        if (request === this.kpiStatusRequest) this.kpiStatusSummary = response;
      } catch (e) {
        console.warn('Could not fetch status summary for KPI:', e);
        if (request === this.kpiStatusRequest) {
          this.kpiStatusSummary = null;
          this.kpiStatusError = 'Não foi possível carregar o resumo de situações.';
        }
      } finally {
        if (request === this.kpiStatusRequest) this.kpiStatusLoading = false;
      }
    },

    async handleKpiFilter(status) {
      if (this.isLoading) return;
      if (!status) {
        this.statusFilter = null;
        this.filtersData.status = null;
        this.filtersData.page = 1;
        // search() reconstrói os filtros a partir do estado local (inclui o
        // tipo selecionado e o escopo de perfil já presente em filtersData).
        await this.search(true);
        return;
      }
      this.statusFilter = status;
      this.filtersData.status = status;
      this.filtersData.page = 1;
      await this.search(true);
    },

    async handleBackordersFilter() {
      if (this.isLoading || this.activeTab !== 0) return;
      this.somenteBackorders = !this.somenteBackorders;
      this.filtersData.page = 1;
      await this.search(true);
    },

    async fetchBackordersCount() {
      const request = ++this.backordersRequest;
      this.backordersLoading = true;
      this.backordersError = '';
      this.totalBackorders = null;
      try {
        const total = await this.$store.dispatch('SLS_PedidosVendas/contarBackorders');
        if (request === this.backordersRequest) this.totalBackorders = total;
      } catch (error) {
        if (request === this.backordersRequest) {
          this.backordersError = error?.message || 'Não foi possível contar os Back Orders.';
        }
      } finally {
        if (request === this.backordersRequest) this.backordersLoading = false;
      }
    },

    async search(preserveFilters = false) {
      const currentFilters = this.activeTab === 0 ? this.getFilters : this.getFiltersRascunhos;
      const filters = {
        ...(preserveFilters ? currentFilters : {}),
        ...this.verificarPerfilUsuario(),
        pageSize: currentFilters?.pageSize,
        ...this.filtersData,
      };
      if (this.somenteBackorders && this.activeTab === 0) {
        filters.somenteBackorders = true;
      } else {
        delete filters.somenteBackorders;
        if (preserveFilters) delete filters.pedidoSituacao;
      }

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null || filters[key] === '') {
          delete filters[key];
        }
      });

      if (this.activeTab === 0) {
        await this.replaceFilters(filters);
        await Promise.all([this.retornaPedidoVendas(), this.fetchBackordersCount(), this.fetchStatusSummaryForKpi()]);
      } else {
        await this.replaceFiltersRascunhos(filters);
        await this.retornaRascunhosPedidoVendas();
      }
    },

    async applyFilters(filters) {
      // O tipo de pedido é controlado pelo filtro do topo (visão exclusiva).
      this.filtersData = { ...filters, tipopedidoid: this.tipoSelecionado, page: 1 };
      if (filters.status) {
        this.statusFilter = filters.status;
      } else {
        this.statusFilter = null;
      }
      await this.search();
    },

    async handleClearFilters() {
      this.filtersData = {
        dataInicial: '',
        dataFinal: '',
        prePedido: null,
        cliente: null,
        razaoSocial: '',
        pedidoErp: null,
        status: null,
        tipopedidoid: this.tipoSelecionado,
      };
      this.statusFilter = null;
      this.somenteBackorders = false;

      if (this.activeTab === 0) {
        await this.replaceFilters(this.tipoSelecionado ? { tipopedidoid: this.tipoSelecionado } : {});
        await Promise.all([this.retornaPedidoVendas(), this.fetchBackordersCount(), this.fetchStatusSummaryForKpi()]);
      } else {
        await this.replaceFiltersRascunhos(this.tipoSelecionado ? { tipopedidoid: this.tipoSelecionado } : {});
        await this.retornaRascunhosPedidoVendas();
      }
    },

    async paginateFiltersPedidos(newFilters) {
      const currentFilters = this.getFilters || {};
      const mergedFilters = { ...currentFilters, ...newFilters };
      await this.setFilters(mergedFilters);
      await this.retornaPedidoVendas();
    },

    async paginateFiltersRascunhos(newFilters) {
      const currentFilters = this.getFiltersRascunhos || {};
      const mergedFilters = { ...currentFilters, ...newFilters };
      await this.setFiltersRascunhos(mergedFilters);
      await this.retornaRascunhosPedidoVendas();
    },

    async changeTab(tabIndex) {
      if (this.isLoading) {
        return;
      }

      this.activeTab = tabIndex;

      if (tabIndex === 0) {
        this.syncVisibleFilters(this.getFilters);
        await Promise.all([this.retornaPedidoVendas(), this.fetchBackordersCount(), this.fetchStatusSummaryForKpi()]);
      } else if (tabIndex === 1) {
        this.syncVisibleFilters(this.getFiltersRascunhos);
        await this.retornaRascunhosPedidoVendas();
      }
    },

    syncVisibleFilters(filters) {
      const source = filters || {};
      if (source.tipopedidoid) {
        this.tipoSelecionado = source.tipopedidoid;
      }
      this.filtersData = {
        dataInicial: source.dataInicial || '',
        dataFinal: source.dataFinal || '',
        prePedido: source.prePedido || null,
        cliente: source.cliente || null,
        razaoSocial: source.razaoSocial || '',
        pedidoErp: source.pedidoErp || null,
        status: source.status || null,
        tipopedidoid: source.tipopedidoid || this.tipoSelecionado,
        page: source.page || 1,
      };
      this.statusFilter = source.status || null;
      this.somenteBackorders = backorderMode(source.somenteBackorders);
    },

    async viewOrderDetails(item) {
      await this.$router.push({
        name: 'pedidos-vendas-detalhes',
        params: { pedidoid: item.pedidoId, clienteid: item.clienteId },
        ...(item._isBackorder ? { query: { bo: item.pedidoNumeroIntegracao } } : {}),
      });
    },

    generatePdf(item) {
      if (this.$refs.modal_pdf_pedido) {
        this.$refs.modal_pdf_pedido.showModalPdf(item);
      }
    },
  },
  async mounted() {
    this.loadViewPreference();
    await this.carregarTiposPermitidos();
    const filtrosPerfil = this.verificarPerfilUsuario();
    if (this.tipoSelecionado) filtrosPerfil.tipopedidoid = this.tipoSelecionado;
    await Promise.all([
      this.replaceFilters(filtrosPerfil),
      this.replaceFiltersRascunhos(filtrosPerfil),
    ]);
    await this.search();
  },
};
</script>

<style scoped>
.pedidos-page {
  padding: 0.5rem 0.75rem;
  min-height: 100vh;
}

.page-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--sf-text-muted);
  text-decoration: none;
  transition: color 0.15s ease;
}

.breadcrumb-link:hover {
  color: var(--sf-accent);
}

.breadcrumb-separator {
  color: var(--sf-text-muted);
}

.breadcrumb-current {
  color: var(--sf-text-secondary);
  font-weight: 500;
}

.tipo-filter-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.tipo-filter-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--sf-text-muted);
}

.tipo-pill {
  border: 1px solid var(--sf-card-border);
  background: var(--sf-card-bg);
  color: var(--sf-text-secondary);
  border-radius: 50px;
  padding: 0.3rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.tipo-pill:hover {
  border-color: var(--sf-accent);
  color: var(--sf-accent);
}

.tipo-pill.active {
  background: var(--sf-accent);
  border-color: var(--sf-accent);
  color: #fff;
}

.content-container {
  background: var(--sf-card-bg);
  border-radius: 8px;
  border: 1px solid var(--sf-card-border);
  overflow: hidden;
  box-shadow: var(--sf-card-shadow);
}

@media (max-width: 768px) {
  .pedidos-page {
    padding: 0.5rem;
  }
}

/* Theme-aware via CSS variables (no extra dark override needed) */
</style>
